import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Collapse, Typography } from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import { useTranslation } from "react-i18next";
import codService from "../../../services/CodService";
import customerServices from "../../../services/CustomerService";
import { CodOrder, CodPaymentTransaction } from "../../../types/codSalesRep";
import { formatCodDate } from "../../../utils/codUtils";
import {
  buildCustomerRows,
  buildInvoiceIndex,
  InvoiceIndex,
  collectionRate,
  ReportCustomerRef,
  ReportDateRange,
  scopeReportData,
  sumTotals,
} from "../../../utils/codReports";
import {
  buildInvoicesSheet,
  buildLinesSheet,
  buildOrdersSheet,
  buildPaymentsSheet,
  exportExcelWorkbook,
  reportFileStamp,
} from "../../../utils/codReportExcel";
import { showToast } from "../../component/common/toastNotification";
import CodSectionHeader from "../../../components/ui/CodSectionHeader";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import { mapServedCustomer } from "./CodCustomerPicker";
import {
  CollectionBar,
  MoneyStat,
  onActivate,
  ReportDetail,
  ReportKpis,
  reportRowHeaderSx,
  reportRowSx,
  ReportToolbar,
} from "./CodReportParts";
import { NJIM } from "./codStyles";
import CodTabBar from "./CodTabBar";
import { RepChartsSection } from "./CodReportCharts";

interface CodReportsTabProps {
  refreshKey?: number;
  repName?: string;
}

/** Every customer assigned to the rep (same source as the customer picker). Best effort. */
const loadAssignedCustomers = async (): Promise<ReportCustomerRef[]> => {
  try {
    const res = await customerServices.searchCustomers("", 1000, true);
    const payload = (res.data ?? {}) as Record<string, unknown>;
    const items = Array.isArray(payload.items)
      ? payload.items
      : Array.isArray(payload.Items)
        ? payload.Items
        : Array.isArray(res.data)
          ? (res.data as unknown[])
          : [];
    return items.map((row) => {
      const customer = mapServedCustomer(row as Record<string, unknown>);
      return {
        customerId: customer.id,
        customerCode: customer.customerCode,
        customerName: customer.customerName,
      };
    });
  } catch {
    return [];
  }
};

const CodReportsTab: React.FC<CodReportsTabProps> = ({ refreshKey = 0, repName = "" }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<CodOrder[]>([]);
  const [payments, setPayments] = useState<CodPaymentTransaction[]>([]);
  const [customers, setCustomers] = useState<ReportCustomerRef[]>([]);
  const [paymentsFailed, setPaymentsFailed] = useState(false);
  // Sage invoices per order (COD money is tracked per invoice). Empty map = order-level fallback.
  const [invoiceIndex, setInvoiceIndex] = useState<InvoiceIndex>(() => new Map());
  const [range, setRange] = useState<ReportDateRange>({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [onlyBalance, setOnlyBalance] = useState(false);
  const [expandedKey, setExpandedKey] = useState("");
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState<"list" | "charts">("list");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [orderRows, paymentRows, customerRows, invoiceOrders] = await Promise.all([
        codService.listOrders(),
        codService.listPaymentTransactions().then(
          (rows) => {
            setPaymentsFailed(false);
            return rows;
          },
          () => {
            setPaymentsFailed(true);
            return [] as CodPaymentTransaction[];
          }
        ),
        loadAssignedCustomers(),
        codService.listInvoiceOrders({ includePaid: true }).catch(() => []),
      ]);
      setInvoiceIndex(buildInvoiceIndex(invoiceOrders));
      setOrders(orderRows);
      setPayments(paymentRows);
      setCustomers(customerRows);
    } catch {
      showToast(t("codSalesRep.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const scoped = useMemo(() => scopeReportData(orders, payments, range), [orders, payments, range]);

  const allRows = useMemo(
    () => buildCustomerRows(scoped.orders, scoped.payments, orders, customers, invoiceIndex),
    [scoped, orders, customers, invoiceIndex]
  );

  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return allRows.filter((row) => {
      if (onlyBalance && row.remaining <= 0.005) return false;
      if (!keyword) return true;
      return (
        row.customerName.toLowerCase().includes(keyword) ||
        row.customerCode.toLowerCase().includes(keyword)
      );
    });
  }, [allRows, search, onlyBalance]);

  const totals = useMemo(() => sumTotals(visibleRows), [visibleRows]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportOrders = visibleRows.flatMap((row) => row.orderList);
      const exportPayments = visibleRows.flatMap((row) => row.paymentList);
      const summaryRows = visibleRows.map((row) => ({
        customerCode: row.customerCode,
        customerName: row.customerName,
        orders: row.orders,
        pending: row.pending,
        approved: row.approved,
        rejected: row.rejected,
        approvedValue: row.approvedValue,
        invoiced: row.invoiced,
        notInvoiced: row.notInvoiced,
        collected: row.collected,
        remaining: row.remaining,
        rate: collectionRate(row),
        pendingValue: row.pendingValue,
        payments: row.payments,
        lastOrder: row.lastOrder,
        lastPayment: row.lastPayment,
      }));

      await exportExcelWorkbook(
        `COD-Customer-Report_${(repName || "rep").replace(/\s+/g, "-")}_${reportFileStamp()}`,
        [
          {
            name: t("codReports.sheetSummary"),
            columns: [
              { key: "customerCode", header: t("codReports.customerCode"), width: 18 },
              { key: "customerName", header: t("codReports.customerName"), width: 36 },
              { key: "orders", header: t("codReports.orders"), type: "int" },
              { key: "pending", header: t("codSalesRep.pendingReview"), type: "int", width: 14 },
              { key: "approved", header: t("codSalesRep.approved"), type: "int" },
              { key: "rejected", header: t("codSalesRep.rejected"), type: "int" },
              { key: "approvedValue", header: t("codReports.approvedValue"), type: "money" },
              { key: "invoiced", header: t("codReports.invoiced"), type: "money" },
              { key: "notInvoiced", header: t("codReports.notInvoiced"), type: "money", width: 20 },
              { key: "collected", header: t("codReports.collected"), type: "money" },
              { key: "remaining", header: t("codReports.remaining"), type: "money" },
              { key: "rate", header: t("codReports.collectionRate"), type: "percent" },
              { key: "pendingValue", header: t("codReports.pendingValue"), type: "money", width: 20 },
              { key: "payments", header: t("codReports.payments"), type: "int" },
              { key: "lastOrder", header: t("codReports.lastOrder"), type: "date" },
              { key: "lastPayment", header: t("codReports.lastPayment"), type: "date" },
            ],
            rows: summaryRows,
            totals: {
              customerCode: t("codReports.totalsRow"),
              orders: totals.orders,
              pending: totals.pending,
              approved: totals.approved,
              rejected: totals.rejected,
              approvedValue: totals.approvedValue,
              invoiced: totals.invoiced,
              notInvoiced: totals.notInvoiced,
              collected: totals.collected,
              remaining: totals.remaining,
              rate: collectionRate(totals),
              pendingValue: totals.pendingValue,
              payments: totals.payments,
            },
          },
          buildOrdersSheet(t, exportOrders, { invoiceIndex }),
          buildInvoicesSheet(t, exportOrders, invoiceIndex),
          buildLinesSheet(t, exportOrders),
          buildPaymentsSheet(t, exportPayments, orders),
        ],
        { rtl: i18n.language?.startsWith("ar"), creator: repName }
      );
      showToast(t("codReports.exportSuccess"), "success");
    } catch {
      showToast(t("codReports.exportFailed"), "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <CodSectionHeader
        title={t("codReports.repTitle")}
        helper={t("codReports.repSubtitle")}
        action={<AssessmentRoundedIcon sx={{ color: NJIM.mint }} />}
      />

      <ReportToolbar
        range={range}
        onRangeChange={setRange}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("codSalesRep.searchCustomers")}
        onlyBalance={onlyBalance}
        onOnlyBalanceChange={setOnlyBalance}
        onExport={handleExport}
        exporting={exporting}
        exportDisabled={loading || visibleRows.length === 0}
      />

      {paymentsFailed && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {t("codReports.paymentsLoadFailed")}
        </Alert>
      )}

      <ReportKpis
        totals={totals}
        countLabel={t("codReports.customers")}
        count={visibleRows.length}
        countIcon={<GroupsRoundedIcon />}
        loading={loading}
      />

      <CodTabBar
        value={view}
        onChange={setView}
        tabs={[
          { value: "list" as const, label: t("codReports.listTab"), icon: <ViewListRoundedIcon sx={{ fontSize: 18 }} /> },
          { value: "charts" as const, label: t("codReports.chartsTab"), icon: <InsertChartRoundedIcon sx={{ fontSize: 18 }} /> },
        ]}
      />

      {view === "charts" && !loading ? (
        <RepChartsSection
          rows={visibleRows}
          orders={visibleRows.flatMap((row) => row.orderList)}
          payments={visibleRows.flatMap((row) => row.paymentList)}
          totals={totals}
        />
      ) : loading ? (
        <TableSkeleton rows={6} columns={5} />
      ) : visibleRows.length === 0 ? (
        <EmptyState title={t("codReports.noData")} description={t("codReports.noDataHint")} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {visibleRows.map((row) => {
            const isOpen = expandedKey === row.key;
            const toggle = () => setExpandedKey(isOpen ? "" : row.key);
            return (
              <Box key={row.key} sx={reportRowSx(isOpen)}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggle}
                  onKeyDown={onActivate(toggle)}
                  sx={{
                    ...reportRowHeaderSx,
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr) auto",
                      md: "minmax(0, 1.6fr) repeat(3, minmax(0, 1fr)) minmax(0, 0.7fr) auto",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CodInitialsAvatar name={row.customerName || row.customerCode} size={40} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontWeight: 700, fontSize: 14, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={row.customerName}
                      >
                        {row.customerName || row.customerCode}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.customerCode} · {t("codReports.orders")}: {row.orders} · {t("codReports.payments")}: {row.payments}
                        {row.lastOrder ? ` · ${t("codReports.lastOrder")} ${formatCodDate(row.lastOrder)}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <MoneyStat label={t("codReports.invoiced")} value={row.invoiced} />
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <MoneyStat label={t("codReports.collected")} value={row.collected} color={NJIM.success} />
                  </Box>
                  <MoneyStat
                    label={t("codReports.remaining")}
                    value={row.remaining}
                    color={row.remaining > 0.005 ? NJIM.danger : NJIM.textMuted}
                  />
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <CollectionBar totals={row} />
                  </Box>
                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      display: { xs: "none", md: "block" },
                      color: NJIM.textSecondary,
                      transform: isOpen ? "rotate(180deg)" : "none",
                      transition: `transform 180ms ${NJIM.easeOut}`,
                    }}
                  />
                </Box>
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ p: { xs: 1.5, md: 2 }, borderTop: `1px solid ${NJIM.border}` }}>
                    <ReportDetail orders={row.orderList} payments={row.paymentList} allOrders={orders} invoiceIndex={invoiceIndex} />
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CodReportsTab;
